import React, { useEffect, useMemo, useRef, useState } from "react";
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Spinner from 'react-bootstrap/Spinner';
import { useFormik } from 'formik';
import { useDispatch } from 'react-redux';
import { cargarArchivoFormSchema, cargarArchivoConTipoGestionSchema } from "../../common/validation-schemas";
import { cargarArchivoAction } from '../../store/actions/carga-masiva-action';
import { cargasPreviasAction } from '../../store/actions/carga-masiva-action';
import { getTiposGestion } from '../../api/carga-masiva-client';
import { aplicaTipoGestion, opcionesTipoGestion } from '../../common/tipos-gestion';
import { Auth } from 'recupero-comunes';

const CargarArchivo = () => {

    const { rutUsuario, rutOrganizacion, paisOrganizacion, roles } = Auth.currentUser();
    const user = { "rutUsrCreacion": rutUsuario, "idOrganizacion": rutOrganizacion, "codPais": paisOrganizacion };

    const fileRef = useRef();
    const [loading, setLoading] = useState(false);
    const dispatch = useDispatch();

    // RG-572: combo Tipo Gestión. Solo aplica si el rol del usuario mapea a algún grupo (IND 314).
    const aplicaTipo = aplicaTipoGestion(roles);
    const [tiposGestion, setTiposGestion] = useState(null); // null = aún cargando
    const [errorTipos, setErrorTipos] = useState("");
    const opcionesTipo = useMemo(() => opcionesTipoGestion(tiposGestion, roles), [tiposGestion]);
    const cargandoTipos = aplicaTipo && tiposGestion === null && !errorTipos;
    // Sin opciones configuradas en BD para el rol, el combo se oculta y la carga se comporta como antes.
    const mostrarTipo = aplicaTipo && (cargandoTipos || !!errorTipos || opcionesTipo.length > 0);
    const tipoPreseleccionado = opcionesTipo.length === 1 ? String(opcionesTipo[0].id) : "";

    // El hook useFormik (formik) permite definir las propiedades del formulario. Devuelve estados y funciones de ayuda.
    const formik = useFormik({

        initialValues: { file: null, tipoGestion: "" }, // Valores iniciales para cada input del formulario
        validationSchema: mostrarTipo ? cargarArchivoConTipoGestionSchema : cargarArchivoFormSchema, // Esquema de validación de Yup para el formulario.
        onSubmit: (values) => { // Función ejecutada al invocar formik.handleSubmit()
          

            // Iniciar 'Cargando...' en botón 'Cargar Archivo'
            setLoading(true);

            // Transformar archivo en Base64
            getBase64(values.file).then((base64data) => {
                
                // Data requerida para cargar archivo. Utilizamos sintaxis spread (...) para concatenar valores de user y datos de archivo. 
                
                // RG-572: tipoGestion solo viaja cuando el combo se muestra; sin él la carga se deriva como siempre.
                let data = { ...user, "filename": values.file.name, "mimetype": values.file.type, "base64data": base64data,
                    ...(mostrarTipo ? { "tipoGestion": values.tipoGestion } : {}) };

                // Invocar a la acción que realiza la carga del archivo
                dispatch(cargarArchivoAction(data)).then(() => {

                    // Reseter el formulario
                    handleReset();

                    // Obtener las cargas previas.
                    let query = btoa(JSON.stringify({ "idOrganizacion": rutOrganizacion, "tipoCarga":"108001" }));
                    dispatch(cargasPreviasAction(query));

                }).catch((error) => {
                    setLoading(false);
                    console.error(error);
                    alert(error.message);
                });
            })
            .catch((error) => {
                setLoading(false);
                console.error(error);
                alert(error.message);
            });
        }
    });

    // Carga las opciones de IND 207 una sola vez, solo si el rol del usuario aplica.
    useEffect(() => {
        if (!aplicaTipo) return;
        getTiposGestion()
            .then(setTiposGestion)
            .catch((error) => {
                console.error(error);
                setErrorTipos("No fue posible cargar los tipos de gestión.");
            });
    }, []);

    // Una sola opción disponible: queda preseleccionada.
    useEffect(() => {
        // Tercer argumento false: no validar, o el error "archivo requerido" aparece al abrir la pantalla.
        if (tipoPreseleccionado) formik.setFieldValue("tipoGestion", tipoPreseleccionado, false);
    }, [tipoPreseleccionado]);

    const handleReset = () => {
        fileRef.current.value = "";
        formik.resetForm({ values: { file: null, tipoGestion: tipoPreseleccionado } });
        setLoading(false);
    }

    return (
        <div>
            <Form noValidate onSubmit={formik.handleSubmit} className="mt-4">

                <div className="d-flex flex-column flex-md-row align-items-md-start gap-4 mb-4">

                <Form.Group className="position-relative flex-grow-1">
                    <Form.Control ref={fileRef} type="file" name="file"
                        onChange={event => { formik.setFieldValue("file", event.currentTarget.files[0]); }} onBlur={formik.handleBlur}
                        isInvalid={!!formik.errors.file}
                        isValid={formik.touched.file && !formik.errors.file}
                    />
                    <Form.Text className="text-muted">Tamaño máximo del archivo: 10MB</Form.Text>
                    <Form.Control.Feedback type="invalid"> {formik.errors.file} </Form.Control.Feedback>
                </Form.Group>

                {mostrarTipo &&
                <Form.Group className="position-relative" style={{ minWidth: "380px" }}>
                    <div className="d-flex align-items-center gap-3">
                        <Form.Label className="mb-0 text-nowrap fw-bold">Tipo Gestión</Form.Label>
                        <Form.Select name="tipoGestion" value={formik.values.tipoGestion}
                            disabled={cargandoTipos}
                            onChange={formik.handleChange} onBlur={formik.handleBlur}
                            isInvalid={!!formik.errors.tipoGestion && (formik.touched.tipoGestion || !!formik.submitCount)}
                        >
                            <option value="">{cargandoTipos ? "Cargando..." : "Seleccione..."}</option>
                            {opcionesTipo.map(t => <option key={t.id} value={String(t.id)}>{t.nombre}</option>)}
                        </Form.Select>
                    </div>
                    {errorTipos && <Form.Text className="text-danger">{errorTipos}</Form.Text>}
                    <Form.Control.Feedback type="invalid" className={formik.errors.tipoGestion && (formik.touched.tipoGestion || !!formik.submitCount) ? "d-block" : ""}> {formik.errors.tipoGestion} </Form.Control.Feedback>
                </Form.Group>
                }

                </div>

                <hr className="mt-4" />

                <div className="d-grid gap-2 d-md-flex justify-content-md-end">
                    <Button className="me-md-2" variant="warning" type="button" onClick={() => { handleReset() }}>Limpiar</Button>
                    <Button variant="danger" type="submit" disabled={loading}>
                        { loading ?
                        <div><Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> Cargando...</div>
                        : "Cargar Archivo" }
                    </Button>
                </div>

            </Form>
        </div>
    )
}

export default CargarArchivo;

const getBase64 = (file) => {
    return new Promise(resolve => {
        let reader = new FileReader();
        // Convert the file to base64 text
        reader.readAsDataURL(file);
        reader.onload = () => {
            resolve(reader.result);
        };
    });
};